import React from "react";
import { Building2, MapPin, CheckCircle, Clock } from "lucide-react";
import ParagraphTag from "./Paragraph";

const ExternalAccountDisplay = ({ externalAccounts, onAddAccount }) => {
  const getStatusIcon = (status) => {
    if (status === "ACTIVE" || status === "VERIFIED") {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    return <Clock className="w-4 h-4 text-yellow-600" />;
  };

  const getStatusColor = (status) => {
    if (status === "ACTIVE" || status === "VERIFIED") {
      return "bg-green-100 text-green-800";
    }
    if (status === "PENDING_VERIFICATION") {
      return "bg-yellow-100 text-yellow-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  if (!externalAccounts || externalAccounts.length === 0) {
    return (
      <div className="mt-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Building2 className="w-5 h-5 text-gray-400 mr-2" />
              <ParagraphTag
                content="No external bank account added yet"
                classes="text-gray-600 font-popinsMedium text-sm"
              />
            </div>
            <button
              type="button"
              className="bg-themeColor text-white px-4 py-2 rounded-md text-sm font-popinsSemiBold hover:bg-themeColor/90 transition-colors"
              onClick={onAddAccount}
            >
              Add Bank Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="mb-3">
        <ParagraphTag
          content="External Bank Account"
          classes="text-darkColor font-popinsSemiBold text-base"
        />
      </div>

      <div className="space-y-3">
        {externalAccounts.map((account, index) => (
          <div
            key={account.id || index}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 transition-all hover:shadow-md"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Account Header */}
                <div className="flex items-center mb-3">
                  <Building2 className="w-6 h-6 text-blue-600 mr-2" />
                  <ParagraphTag
                    content={account.type || "BANK ACCOUNT"}
                    classes="text-blue-600 font-popinsBold text-sm"
                  />
                  {account.status && (
                    <span
                      className={`ml-3 px-2 py-1 text-xs font-popinsSemiBold rounded flex items-center ${getStatusColor(
                        account.status
                      )}`}
                    >
                      {getStatusIcon(account.status)}
                      <span className="ml-1">{account.status}</span>
                    </span>
                  )}
                </div>

                {/* Account Details */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <ParagraphTag
                      content="Account Number:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={
                        account.accountNumberLast4
                          ? `••••${account.accountNumberLast4}`
                          : "••••••••"
                      }
                      classes="text-darkColor font-popinsSemiBold text-sm"
                    />
                  </div>

                  <div className="flex items-center">
                    <ParagraphTag
                      content="Account Holder:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={account.holderName}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  <div className="flex items-center">
                    <ParagraphTag
                      content="Holder Type:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={account.holderType}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  <div className="flex items-center">
                    <ParagraphTag
                      content="Routing Number:"
                      classes="text-gray-600 font-popinsMedium text-xs mr-2"
                    />
                    <ParagraphTag
                      content={account.routingNumber || "N/A"}
                      classes="text-darkColor font-popinsMedium text-sm"
                    />
                  </div>

                  {account.wireRoutingNumber && (
                    <div className="flex items-center">
                      <ParagraphTag
                        content="Wire Routing:"
                        classes="text-gray-600 font-popinsMedium text-xs mr-2"
                      />
                      <ParagraphTag
                        content={account.wireRoutingNumber}
                        classes="text-darkColor font-popinsMedium text-sm"
                      />
                    </div>
                  )}

                  {/* Bank Info */}
                  {account.bankInfo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-gray-500 mr-2 mt-0.5" />
                        <div>
                          <ParagraphTag
                            content="Bank:"
                            classes="text-gray-600 font-popinsMedium text-xs mb-1"
                          />
                          <ParagraphTag
                            content={account.bankInfo.name || "N/A"}
                            classes="text-darkColor font-popinsRegular text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Reason */}
                  {account.statusReason && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <ParagraphTag
                        content={`Status: ${account.statusReason}`}
                        classes="text-gray-500 font-popinsRegular text-xs"
                      />
                    </div>
                  )}

                  {/* Created/Updated dates */}
                  {(account.createdOn || account.lastUpdatedOn) && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      {account.createdOn && (
                        <ParagraphTag
                          content={`Added: ${new Date(
                            account.createdOn
                          ).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}`}
                          classes="text-gray-500 font-popinsRegular text-xs"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExternalAccountDisplay;

